import {
    Text,
    View,
    StyleSheet,
    ViewProps,
} from '@react-pdf/renderer';

import {
    MAX_LENGTH_PDF_INDIVIDUAL_TABLE_VALUE_SIZE,
    MAX_LENGTH_PDF_INDIVIDUAL_TABLE_AVERAGE_VALUE_SIZE,
} from 'constant/report';
import { NullableNumber } from 'types/Reports';

import { handlePDFValueOverflow } from '../report/utils/report';
import { computeHorizontalBarLength } from "../report/utils/reportCompute";
import { convertHoursAndMinutesToHours } from '../report/utils/reportFormat';
import { breakWordsForLongLine } from '../report/utils/reportString';

const individualTableStyles = StyleSheet.create({
    individualTable: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
    },
    individualTableRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
    },
    individualTableHeaderName: {
        width: '20%',
        fontFamily: 'Roboto',
        fontSize: 12,
        fontWeight: 400,
        color: '#333333',
        textAlign: 'left',
    },
    individualTableHeaderValue: {
        width: '30%',
        fontFamily: 'Roboto',
        fontSize: 12,
        fontWeight: 400,
        color: '#333333',
        textAlign: 'left',
    },
    individualTableHeaderValueWide: {
        width: '40%',
        fontFamily: 'Roboto',
        fontSize: 12,
        fontWeight: 400,
        color: '#333333',
        textAlign: 'left',
    },
    individualTableHeaderDate: {
        width: '30%',
        fontFamily: 'Roboto',
        fontSize: 12,
        fontWeight: 400,
        color: '#333333',
        textAlign: 'right',
    },
    individualTableHeaderDateWide: {
        width: '40%',
        fontFamily: 'Roboto',
        fontSize: 12,
        fontWeight: 400,
        color: '#333333',
        textAlign: 'right',
    },
    individualTableHeaderAverage: {
        width: '20%',
        fontFamily: 'Roboto',
        fontSize: 12,
        fontWeight: 400,
        color: '#333333',
        textAlign: 'right',
    },
    individualTableCellName: {
        width: '20%',
        fontFamily: 'Roboto',
        fontSize: 12,
        fontWeight: 400,
        color: '#636363',
        textAlign: 'left',
        paddingRight: '10px',
    },
    individualTableCellValue: {
        width: '20%',
        fontFamily: 'Roboto',
        fontSize: 12,
        fontWeight: 400,
        color: '#333333',
        textAlign: 'left',
    },
    individualTableCellBar: {
        width: '20%',
        height: 12,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingTop: '3px',
        paddingLeft: '3px',
    },
    individualTableCellBarWide: {
        width: '20%',
        height: 12,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingTop: '3px',
        paddingLeft: '3px',
    },
    individualTableCellDate: {
        width: '20%',
        fontFamily: 'Roboto',
        fontSize: 12,
        fontWeight: 400,
        color: '#636363',
        textAlign: 'right',
    },
    individualTableCellDateWide: {
        width: '40%',
        fontFamily: 'Roboto',
        fontSize: 12,
        fontWeight: 400,
        color: '#636363',
        textAlign: 'right',
    },
    individualTableCellAverage: {
        width: '20%',
        fontFamily: 'Roboto',
        fontSize: 12,
        fontWeight: 400,
        color: '#636363',
        textAlign: 'right',
        paddingLeft: '3px',
    },
});

type ReportPDFIndividualTableProps = {
    maxValue: NullableNumber;
    minValue: NullableNumber;

    color: string;
    unit: string;

    headers: string[];
    rows: string[][];
    showAverage?: boolean;
};

const ReportPDFIndividualTable = ({
    maxValue,
    minValue,
    color,
    unit,
    headers,
    rows,
    showAverage = true,
}: ReportPDFIndividualTableProps) => {
    const barHeight = 10;
    let unitStr = ` ${unit}`;

    if (rows.length > 0 && rows[0][1].includes('h')) {
        unitStr = '';
    }

    const headerValueStyle = showAverage
        ? individualTableStyles.individualTableHeaderValue
        : individualTableStyles.individualTableHeaderValueWide;
    const headerDateStyle = showAverage
        ? individualTableStyles.individualTableHeaderDate
        : individualTableStyles.individualTableHeaderDateWide;
    const cellBarStyle = showAverage
        ? individualTableStyles.individualTableCellBar
        : individualTableStyles.individualTableCellBarWide;
    const cellDateStyle = showAverage
        ? individualTableStyles.individualTableCellDate
        : individualTableStyles.individualTableCellDateWide;

    return (
        <View style={individualTableStyles.individualTable}>
            {/* headers */}
            <View style={individualTableStyles.individualTableRow}>
                <Text style={individualTableStyles.individualTableHeaderName}>{headers[0]}</Text>
                <Text style={headerValueStyle}>{headers[1]}</Text>
                <Text style={headerDateStyle}>{headers[2]}</Text>
                {showAverage && <Text style={individualTableStyles.individualTableHeaderAverage}>{headers[3]}</Text>}
            </View>

            {/* rows */}
            {
                rows.map((row, index) => {
                    const additionalStyles: Partial<ViewProps["style"]> = {};
                    if (index === 0) {
                        additionalStyles.marginTop = 10;
                    }

                    let rowValue = Number.parseFloat(row[1]);

                    if (row[1].includes('h')) {
                        rowValue = convertHoursAndMinutesToHours(row[1]);
                    }

                    const maxWidth = 100;
                    const width = computeHorizontalBarLength(maxValue, minValue, rowValue, maxWidth);

                    const valueDisplay = handlePDFValueOverflow(row[1], MAX_LENGTH_PDF_INDIVIDUAL_TABLE_VALUE_SIZE, 0);

                    const valueRow = `${valueDisplay}${unitStr}`;
                    const bar = <View style={{ height: barHeight, width, backgroundColor: color }}></View>;

                    return (
                        <View style={{...individualTableStyles.individualTableRow, ...additionalStyles}} wrap={false} key={`${row[0]}${row[1]}${index}`}>
                            <Text style={individualTableStyles.individualTableCellName}>
                                {breakWordsForLongLine(row[0])}
                            </Text>
                            <Text style={individualTableStyles.individualTableCellValue}>
                                {valueRow}
                            </Text>
                            <View style={cellBarStyle}>
                                {bar}
                            </View>
                            <Text style={cellDateStyle}>
                                {row[2]}
                            </Text>
                            {showAverage && <Text style={individualTableStyles.individualTableCellAverage}>
                                {handlePDFValueOverflow(row[3], MAX_LENGTH_PDF_INDIVIDUAL_TABLE_AVERAGE_VALUE_SIZE, 0)}{unitStr}
                            </Text>}
                        </View>
                    );
                })
            }
        </View>
    );
};

export default ReportPDFIndividualTable;
